import DownloadIcon from '@mui/icons-material/Download';
import { Button, CircularProgress, type SxProps, type Theme } from '@mui/material';
import { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCanAccess, useNotify } from 'react-admin';
import { useReportMerchantOfferSummaryCreate } from '../../../../DataProvider';
import { getJsonApiErrorDetail } from '../../../../utils';
import {
  downloadMerchantsOfferSummariesCsv,
  getMerchantOfferSummaryReportParams,
  type Filter,
} from './utils';

export interface ExportButtonProps {
  sx?: SxProps<Theme>;
}

export default function ExportButton({ sx }: ExportButtonProps): ReactElement | null {
  const { handleSubmit } = useFormContext();
  const notify = useNotify();
  const [runReport, report] = useReportMerchantOfferSummaryCreate();

  const reportsCreateAccess = useCanAccess({ resource: 'reports', action: 'create' });
  const merchantsOfferSummariesListAccess = useCanAccess({
    resource: 'merchants_offer_summaries',
    action: 'list',
  });

  const handleExport = handleSubmit(async (values) => {
    if (report.isPending) {
      return;
    }

    try {
      const filter = values as Filter;
      const result = await runReport(getMerchantOfferSummaryReportParams(filter));
      downloadMerchantsOfferSummariesCsv(result.data, result.prefetched);
      notify('Export downloaded', { type: 'success' });
    } catch (error) {
      notify(getJsonApiErrorDetail(error, 'Export failed'), { type: 'error' });
    }
  });

  if (!reportsCreateAccess.canAccess || !merchantsOfferSummariesListAccess.canAccess) {
    return null;
  }

  return (
    <Button
      variant="contained"
      onClick={() => void handleExport()}
      disabled={report.isPending}
      sx={sx}
      startIcon={report.isPending ? <CircularProgress size={16} /> : <DownloadIcon />}
    >
      {report.isPending ? 'Exporting…' : 'Export CSV'}
    </Button>
  );
}
